<template>
  <MainLayout class="evaluations">
    <div class="container mw-960" slot="sub-header">
      <div class="row">
        <div class="col-12 hidden-xs-down">
          <div class="sub-header-btn-row">
            <button class="btn btn-outline-primary mr-1"
                    v-on:click="evaluationCriteriaModalIsShown = true"
                    v-bind:disabled="noClass">
              {{ $t('evaluations.sub_header.config_criteria') }}
            </button>
            <button class="btn btn-primary ml-1 mr-1" v-on:click="save" v-bind:disabled="noClass">
              <template v-if="!busy">{{ $t('common.save') }}</template>
              <LoadingBounce v-else></LoadingBounce>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="container mw-960 mb-5 pb-20 text-center" slot="layout-body" v-if="noClass">
      <div class="typography--body-2 typography--color-disabled pt-5 mt-5"
           v-html="$t('evaluations.no_classes')">
      </div>
    </div>

    <div class="container mw-960 pb-20" slot="layout-body" v-else>
      <div class="row browse-filter-row">
        <div class="col-12 col-lg-7">
          <div class="row box">
            <div class="col-sm-5 divider">
              <b-dropdown v-bind:text="selectedClassName" class="mnv-dropdown">
                <b-dropdown-item v-for="cls in managingClassList"
                                 v-on:click="selectClass(cls.id)" v-bind:key="cls.id"
                                 v-bind:disabled="loading">
                  {{ cls.year }} {{ cls.grade }} - {{ cls.name }}
                </b-dropdown-item>
              </b-dropdown>
            </div>

            <div class="col-sm-3">
              <b-dropdown v-bind:text="subjectMap[selectedSubjectId]" class="mnv-dropdown subject-select">
                <b-dropdown-item v-for="(subjectName, sjId) in subjectMap"
                                 v-on:click="selectSubject(sjId)"
                                 v-bind:key="sjId"
                                 v-bind:disabled="loading || (sjId !== '2' && sjId !== '3' && sjId !== '5')">
                  {{ subjectMap[sjId] }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-5">
          <ul class="nav justify-content-end order-by-btns">
            <li class="nav-item">
              <a class="nav-link typography--color-secondary" 
                 v-bind:class="{ active: evaluationOrderBy === 'number' }"
                 v-on:click="selectOrderBy('number')">{{ $t('evaluations.filters.order_by.number') }}</a>
            </li>
            <li class="nav-item">
              <a class="nav-link typography--color-secondary" 
                 v-bind:class="{ active: evaluationOrderBy === 'name' }"
                 v-on:click="selectOrderBy('name')">{{ $t('evaluations.filters.order_by.name') }}</a>
            </li>
          </ul>
        </div>
      </div>

      <DomainSelect
        v-bind:domains="domains"
        v-bind:selected-domain-ids="selectedDomainIds"
        v-bind:loading="loading"
        v-on:domain-option-clicked="onDomainOptionClicked"
      />

      <div v-if="loading">
        <LoadingBounce />
      </div>

      <EvaluationTable
        v-bind:evaluation-list="sortedEvaluationList"
        v-bind:selected-domain-ids="selectedDomainIds"
        v-else
      />
    </div>

    <div slot="modal-area">
      <EvaluationCriteriaModal
        v-on:criteria-updated="onCriteriaUpdated"
        v-on:cancel="evaluationCriteriaModalIsShown = false"
        v-if="evaluationCriteriaModalIsShown"
      />

      <Snackbar />

      <!-- <EvaluationServiceGoneModal
        v-on:ok="evaluationServiceGoneModalIsShown = false"
        v-if="evaluationServiceGoneModalIsShown"
      /> -->
    </div>
  </MainLayout>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { sortBy } from 'lodash';
  import EvaluationServiceGoneModal from '../components/EvaluationServiceGoneModal';
  import EvaluationCriteriaModal from '../components/EvaluationCriteriaModal';
  import EvaluationTable from '../components/EvaluationTable';
  import DomainSelect from '../components/DomainSelect';

  export default {
    name: 'evaluations',

    components: {
      EvaluationServiceGoneModal,
      EvaluationCriteriaModal,
      EvaluationTable,
      DomainSelect,
    },

    data() {
      return {
        evaluationCriteriaModalIsShown: false,

        evaluationServiceGoneModalIsShown: true,

        noClass: false,

        loading: false,

        busy: false,
      };
    },

    computed: {
      ...mapGetters({
        selectedClassDomainIds: 'selectedClassEvaluationDomainIds',
        classDomains: 'classEvaluationDomains',
        evaluationFilters: 'evaluationFilters',
        evaluationOrderBy: 'evaluationOrderBy',
        managingClassList: 'managingClassList',
        managingClasses: 'managingClasses',
        subjectMap: 'evaluationSubjectMap',
        evaluationList: 'evaluationList',
      }),

      sortedEvaluationList() {
        return sortBy(this.evaluationList, this.evaluationOrderBy);
      },

      domains() {
        return this.classDomains[this.selectedClassId] || [];
      },

      selectedDomainIds() {
        return this.selectedClassDomainIds[this.selectedClassId] || [];
      },

      selectedSubjectId() {
        return this.evaluationFilters.subject_id;
      },

      selectedClassId() {
        return this.evaluationFilters.class_id;
      },

      selectedClassName() {
        if (this.managingClasses[this.selectedClassId]) {
          const selectedClass = this.managingClasses[this.selectedClassId];

          return `${selectedClass.year} ${selectedClass.grade} - ${selectedClass.name}`;
        }

        return '';
      },
    },

    methods: {
      ...mapActions([
        'setSelectedEvaluationDomainIds',
        'fetchEvaluationDomains',
        'saveEvaluationDomains',
        'setEvaluationOrderBy',
        'setEvaluationFilter',
        'fetchEvaluationList',
        'saveEvaluationList',
      ]),

      selectSubject(subjectId) {
        const parsedSubjectId = parseInt(subjectId, 10);

        if (parsedSubjectId !== this.selectedSubjectId) {
          this.loading = true;

          this.setEvaluationFilter({ property: 'subject_id', value: parsedSubjectId })
            .then(() => (
              this.fetchDomains()
            ))
            .then(() => (
              this.fetchEvaluations()
            ))
            .then(() => {
              this.loading = false;
            });
        }
      },

      selectClass(classId) {
        if (classId !== this.selectedClassId) {
          this.loading = true;

          this.setEvaluationFilter({ property: 'class_id', value: classId })
            .then(() => (
              this.fetchDomains()
            ))
            .then(() => (
              this.fetchEvaluations()
            ))
            .then(() => {
              this.loading = false;
            });
        }
      },

      selectOrderBy(orderBy) {
        this.setEvaluationOrderBy(orderBy);
      },

      fetchEvaluations() {
        return this.fetchEvaluationList({
          classId: this.selectedClassId,
          filters: {
            ...this.evaluationFilters,
            domains: this.selectedDomainIds.join(','),
          },
        });
      },

      fetchDomains() {
        return this.fetchEvaluationDomains({
          classId: this.selectedClassId,
          filters: this.evaluationFilters,
        });
      },

      onDomainOptionClicked(domainId) {
        this.loading = true;

        Promise.resolve()
          .then(() => {
            if (this.selectedDomainIds.indexOf(domainId) === -1) {
              return this.setSelectedEvaluationDomainIds({
                classId: this.selectedClassId,
                selectedDomainIds: [
                  ...this.selectedDomainIds,
                  domainId,
                ],
              });
            }

            return this.setSelectedEvaluationDomainIds({
              classId: this.selectedClassId,
              selectedDomainIds: this.selectedDomainIds.filter(id => (
                id !== domainId
              )),
            });
          })
          .then(() => (
            this.fetchEvaluations()
          ))
          .then(() => {
            this.loading = false;
          });
      },

      onCriteriaUpdated() {
        this.loading = true;
        this.evaluationCriteriaModalIsShown = false;

        this.fetchEvaluations()
          .then(() => {
            this.loading = false;
          });
      },

      save() {
        this.busy = true;

        Promise.all([
          this.saveEvaluationDomains({
            classId: this.selectedClassId,
            domainBody: {
              subject_id: this.selectedSubjectId,
              domains: this.selectedDomainIds.join(','),
            },
          }),
          this.saveEvaluationList({
            classId: this.selectedClassId,
            evaluationList: this.evaluationList,
          }),
        ])
        .then(() => {
          this.busy = false;
        });
      },
    },

    created() {
      if (this.managingClassList.length === 0) {
        this.noClass = true;

        return;
      }

      this.loading = true;

      Promise.resolve()
        .then(() => {
          if (!this.selectedClassId && this.managingClassList.length > 0) {
            return this.setEvaluationFilter({ property: 'class_id', value: this.managingClassList[0].id });
          }

          return true;
        })
        .then(() => {
          if (!this.selectedSubjectId) {
            return this.setEvaluationFilter({ property: 'subject_id', value: 2 });
          }

          return true;
        })
        .then(() => (
          this.fetchDomains()
        ))
        .then(() => (
          this.fetchEvaluations()
        ))
        .then(() => {
          this.loading = false;
        });
    },
  };
</script>

<style lang="scss">
  .evaluations {
    .browse-filter-row {
      &>.col-12 {
        padding-top: 7px;
        padding-bottom: 7px;
      }

      .nav {
        padding-top: 6px;
        padding-bottom: 6px;
      }

      .mnv-dropdown .dropdown-toggle,
      .nav-item .nav-link {
        line-height: 1.29;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/evaluations/views/Evaluations.vue